"use client";

import * as React from "react";
import Link from "next/link";

import { Container } from "@/app/_components/layout/container";
import { stepsBlock } from "@/lib/content/homepage";
import { trackEvent } from "@/lib/analytics/dataLayer";
import { cn } from "@/lib/utils";

function useActiveStep(
  ref: React.RefObject<HTMLElement | null>,
  total: number,
): number {
  const [active, setActive] = React.useState(0);

  React.useEffect(() => {
    const node = ref.current;
    if (!node) return;

    let raf = 0;
    const HYSTERESIS = 0.04;
    const THRESHOLDS = Array.from(
      { length: total - 1 },
      (_, i) => (i + 1) / total,
    );

    const compute = () => {
      raf = 0;
      const rect = node.getBoundingClientRect();
      const vh = window.innerHeight;
      const startOffset = vh * 0.85;
      const endOffset = vh * 0.3;
      const range = startOffset - endOffset + rect.height;
      const traveled = startOffset - rect.top;
      const progress = Math.max(0, Math.min(1, traveled / range));

      let rawStep = 0;
      for (let i = 0; i < THRESHOLDS.length; i++) {
        if (progress >= THRESHOLDS[i]) rawStep = i + 1;
      }
      if (rawStep > total - 1) rawStep = total - 1;

      setActive((prev) => {
        if (rawStep === prev) return prev;
        if (rawStep > prev) {
          const boundary = THRESHOLDS[rawStep - 1];
          return progress >= boundary + HYSTERESIS ? rawStep : prev;
        }
        const boundary = THRESHOLDS[rawStep];
        return progress <= boundary - HYSTERESIS ? rawStep : prev;
      });
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [ref, total]);

  return active;
}

export function StepsProcess() {
  const rowRef = React.useRef<HTMLOListElement>(null);
  const total = stepsBlock.steps.length;
  const activeStep = useActiveStep(rowRef, total);

  return (
    <section
      aria-label="Prozess"
      className="bg-background text-clarus-dark py-20 md:py-28"
    >
      <Container>
        <div className="rounded-card border-[1.5px] border-clarus-dark bg-clarus-green p-6 md:p-14 lg:p-16">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-display-lg text-clarus-dark">
              {stepsBlock.heading}
            </h2>
            <p className="text-subline mt-3 text-clarus-dark/80">
              {stepsBlock.subline}
            </p>
          </div>

          <div className="relative mt-12 md:mt-20">
            {/* Horizontal timeline (desktop only) — runs from center of
                circle 1 (12.5%) to center of circle 4 (87.5%). */}
            <div
              aria-hidden="true"
              className="absolute left-[12.5%] right-[12.5%] top-6 hidden h-px bg-clarus-dark lg:block"
            />

            <ol
              ref={rowRef}
              className="relative flex flex-col gap-12 lg:grid lg:grid-cols-4 lg:gap-8"
            >
              {stepsBlock.steps.map((step, i) => {
                const isActive = i === activeStep;
                const isLast = i === total - 1;
                return (
                  <li
                    key={step.number}
                    className="relative flex items-start gap-5 text-left lg:flex-col lg:items-center lg:gap-0 lg:text-center"
                  >
                    {/* Per-segment vertical line (mobile/tablet only) —
                        runs from bottom of this circle to top of the next
                        circle, bridging the gap-12 (48px) between items.
                        Hidden on last item so the line ends at circle 4. */}
                    {!isLast && (
                      <span
                        aria-hidden="true"
                        className="absolute left-6 top-12 bottom-[-48px] w-px bg-clarus-dark lg:hidden"
                      />
                    )}
                    <span
                      className={cn(
                        "relative z-10 inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-[1.5px] border-clarus-dark transition-colors duration-[850ms] ease-[cubic-bezier(0.22,1,0.36,1)]",
                        isActive
                          ? "bg-clarus-dark text-clarus-yellow"
                          : "bg-white text-clarus-dark",
                      )}
                      style={{
                        fontFamily: "var(--font-display)",
                        fontWeight: 900,
                        fontSize: "1.125rem",
                        lineHeight: 1,
                      }}
                      aria-hidden="true"
                      aria-current={isActive ? "step" : undefined}
                    >
                      {step.number}
                    </span>
                    <div className="min-w-0 pt-2 lg:pt-0">
                      <h3
                        className="font-extrabold text-clarus-dark lg:mt-6"
                        style={{
                          fontFamily: "var(--font-display)",
                          fontSize: "1.0625rem",
                        }}
                      >
                        <span className="sr-only">Schritt {step.number}: </span>
                        {step.title}
                      </h3>
                      <p className="text-annotation mt-2 text-clarus-dark/75 lg:max-w-[230px]">
                        {step.body}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>

        <div className="mt-8 flex justify-center lg:mt-4 lg:justify-end">
          <Link
            href={stepsBlock.cta.href}
            onClick={() => trackEvent("cta_scan", { location: "steps" })}
            className="text-annotation font-medium text-clarus-dark underline underline-offset-4 hover:text-clarus-dark/70"
          >
            {stepsBlock.cta.label}
          </Link>
        </div>
      </Container>
    </section>
  );
}
